/**
 * Activity table column schema — maps every check (1–37) to its per-check
 * column definitions per Section 7 of Prompt 7B.
 *
 * Each column has:
 *   - key      — stable identifier used by the renderer / XLSX writer
 *   - label    — header text shown in the PDF column header
 *   - extract  — (finding) => raw value (JS primitive or Date); undefined → '—'
 *   - align    — 'left' | 'right' (default 'left')
 *
 * The renderer layers these on top of the 5 base columns which are identical
 * for every check.
 *
 * @module pdf/helpers/activity-table-column-schema
 */

import { formatDateDDMonYYYY } from '../../utils/dates.js';

// ==================== BASE COLUMNS ====================

/**
 * Base columns — rendered for every check, always in this order, always first.
 */
export const BASE_COLUMNS = [
  {
    key: 'activityId',
    label: 'Activity ID',
    extract: (f) => f.taskCode ?? '',
    align: 'left',
  },
  {
    key: 'activityName',
    label: 'Activity Name',
    extract: (f) => f.taskName ?? '',
    align: 'left',
  },
  {
    key: 'wbsPath',
    label: 'WBS Path',
    extract: (f) => f.wbsPath ?? '',
    align: 'left',
  },
  {
    key: 'totalFloatDays',
    label: 'Total Float (d)',
    extract: (f) => (f.totalFloatDays === null || f.totalFloatDays === undefined)
      ? null
      : Number(f.totalFloatDays),
    align: 'right',
    kind: 'float',
  },
  {
    key: 'durationDays',
    label: 'Duration (d)',
    extract: (f) => (f.durationDays === null || f.durationDays === undefined)
      ? null
      : Number(f.durationDays),
    align: 'right',
  },
];

// ==================== PER-CHECK COLUMN DEFINITIONS ====================

// C-1 (V-16): activity-table date cells render the product-wide "dd Mon yyyy"
// format via the shared formatter (was ISO `${y}-${m}-${day}` — the last date
// surface still leaking ISO after A1's list/cover sweep). formatDateDDMonYYYY
// uses the same LOCAL accessors, preserving the DC1 (Finding C) wall-clock
// invariant: a P6 00:00 local stamp must not shift a day west of UTC in these
// cells. Empty / invalid input stays '' (not the '—' the shared formatter
// returns) so blank date columns render blank, byte-identical to before.
export const fmtDate = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '';
  return formatDateDDMonYYYY(dt);
};

// F5 (D-Check11LogicDate, v3.36): #11 emits the logic-calculated date under
// `predecessorEarlyEnd` (FS start conflict) / `predecessorEarlyStart` (SS start
// conflict) / `logicDrivenFinish` (finish conflict) — the schema previously
// read the never-emitted `logicDate` / `conflictDeltaDays`, so both the
// Logic-Calc Date and Δ Days columns rendered blank on every #11 finding across
// the dashboard ActivityTable and the XLSX (both consume this schema). These
// helpers resolve the correct key and compute the signed day gap.
const MS_PER_CAL_DAY = 24 * 60 * 60 * 1000;
const toParsedDate = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};
// The logic-calculated date a #11 conflict compares its constraint against.
const check11LogicDate = (f) =>
  f?.details?.predecessorEarlyEnd
  || f?.details?.predecessorEarlyStart
  || f?.details?.logicDrivenFinish
  || null;
// Whole calendar days from the constraint date to the logic date; positive =
// logic later than the constraint (the conflict direction). Null when either
// date is missing or unparseable.
const check11ConflictDeltaDays = (f) => {
  const c = toParsedDate(f?.details?.constraintDate);
  const l = toParsedDate(check11LogicDate(f));
  if (!c || !l) return null;
  return Math.round((l.getTime() - c.getTime()) / MS_PER_CAL_DAY);
};

const REL_TYPE_LABEL = {
  PR_FS: 'FS', PR_SS: 'SS', PR_FF: 'FF', PR_SF: 'SF',
};
const friendlyRel = (t) => REL_TYPE_LABEL[t] || t || '';

const CSTR_LABEL = {
  CS_MSO: 'MSO', CS_MFO: 'MFO', CS_MEO: 'MEO',
  CS_MANDSTART: 'Mand Start', CS_MANDFIN: 'Mand Finish',
  CS_SNET: 'SNET', CS_SNLT: 'SNLT', CS_FNET: 'FNET', CS_FNLT: 'FNLT',
  CS_MSOA: 'MSOA', CS_MSOB: 'MSOB', CS_MEOA: 'MEOA', CS_MEOB: 'MEOB',
  CS_ALAP: 'ALAP',
  CS_UNKNOWN: 'Inferred',
};
const friendlyCstr = (t) => CSTR_LABEL[t] || t || '';

const EXTRA_COLUMNS = {
  1: [],
  2: [],
  3: [
    { key: 'predecessorId',   label: 'Predecessor ID',   extract: (f) => f.details?.predecessorCode ?? '' },
    { key: 'relationshipType',label: 'Rel Type',         extract: (f) => friendlyRel(f.details?.relationshipType) },
    { key: 'lagDays',         label: 'Lag (d)',          extract: (f) => (f.details?.lagDays === undefined || f.details?.lagDays === null) ? null : Number(f.details.lagDays), align: 'right' },
  ],
  4: [
    { key: 'connectionType',  label: 'Connection',       extract: (f) => {
      const t = f.details?.danglingType;
      if (t === 'start') return 'start-only';
      if (t === 'finish') return 'finish-only';
      return t || '';
    } },
  ],
  5: [
    { key: 'impliedPath',     label: 'Implied-Through Path', extract: (f) => Array.isArray(f.details?.alternativePath) ? f.details.alternativePath.join(' → ') : '' },
  ],
  6: [
    { key: 'loopPath',        label: 'Loop Path',        extract: (f) => {
      const arr = f.details?.loopActivities;
      if (!Array.isArray(arr) || arr.length === 0) return '';
      return arr.join(' → ') + ' → ' + arr[0];
    } },
  ],
  7: [
    { key: 'constraintType',  label: 'Constraint Type',  extract: (f) => friendlyCstr(f.details?.constraintType) },
    { key: 'constraintDate',  label: 'Constraint Date',  extract: (f) => fmtDate(f.details?.constraintDate) },
  ],
  8: [
    { key: 'constraintType',  label: 'Constraint Type',  extract: (f) => friendlyCstr(f.details?.constraintType) },
    { key: 'constraintDate',  label: 'Constraint Date',  extract: (f) => fmtDate(f.details?.constraintDate) },
    { key: 'inferredFlag',    label: 'Inferred',         extract: (f) => f.details?.inferredConstraint ? '⚠' : '', align: 'center' },
  ],
  9: [],
  10: [
    { key: 'constraintType',  label: 'Constraint Type',  extract: (f) => friendlyCstr(f.details?.constraintType) },
    { key: 'constraintDate',  label: 'Constraint Date',  extract: (f) => fmtDate(f.details?.constraintDate) },
    { key: 'daysBeforeDataDate', label: 'Days Before DD', extract: (f) => f.details?.daysPastDataDate ?? null, align: 'right' },
  ],
  11: [
    { key: 'constraintDate',     label: 'Constraint Date', extract: (f) => fmtDate(f.details?.constraintDate) },
    { key: 'logicCalcDate',      label: 'Logic-Calc Date', extract: (f) => fmtDate(check11LogicDate(f)) },
    { key: 'conflictDelta',      label: 'Δ Days',          extract: (f) => check11ConflictDeltaDays(f), align: 'right' },
  ],
  12: [
    { key: 'taskType',           label: 'Activity Type',   extract: (f) => friendlyTaskType(f.details?.taskType) },
  ],
  13: [
    { key: 'excessDays',         label: 'Excess (d)',      extract: (f) => {
      const hpd = f.details?.hoursPerDay || 8;
      const excessH = f.details?.excessHours;
      return (excessH == null) ? null : Math.round((excessH / hpd) * 10) / 10;
    }, align: 'right' },
  ],
  14: [],
  15: [],
  16: [],
  17: [],
  18: [],
  19: [],
  20: [
    { key: 'breakPoint',         label: 'Break Point',     extract: (f) => f.taskCode ?? '' },
    { key: 'breakType',          label: 'Break Type',      extract: (f) => f.details?.segmentIndex ? `segment ${f.details.segmentIndex}` : '' },
  ],
  21: [],
  22: [
    { key: 'cpIdentifier',       label: 'CP ID',           extract: (f) => f.details?.segmentIndex ? `CP ${f.details.segmentIndex}` : '' },
    { key: 'cpStart',            label: 'CP Start',        extract: (f) => {
      const arr = f.details?.segmentActivities;
      return Array.isArray(arr) && arr.length > 0 ? arr[0] : '';
    } },
    { key: 'cpEnd',              label: 'CP End',          extract: (f) => {
      const arr = f.details?.segmentActivities;
      return Array.isArray(arr) && arr.length > 0 ? arr[arr.length - 1] : '';
    } },
  ],
  23: [
    { key: 'cpConstraintType',   label: 'Constraint on CP', extract: (f) => friendlyCstr(f.details?.constraintType) },
  ],
  24: [
    { key: 'calendarId',         label: 'Calendar ID',     extract: (f) => f.details?.calendarId ?? '' },
    { key: 'calendarName',       label: 'Calendar Name',   extract: (f) => f.details?.calendarName ?? '' },
  ],
  25: [
    { key: 'calendarName',       label: 'Calendar Name',   extract: (f) => f.details?.activityCalendarName ?? '' },
    { key: 'predCalendarName',   label: 'Pred Calendar',   extract: (f) => f.details?.predecessorCalendarName ?? '' },
  ],
  26: [
    { key: 'earlyFinish',        label: 'Early Finish',    extract: (f) => fmtDate(f.details?.earlyEndDate) },
    { key: 'daysBehind',         label: 'Days Behind',     extract: (f) => f.details?.daysBehind ?? null, align: 'right' },
  ],
  27: [
    { key: 'actualStart',        label: 'Actual Start',    extract: (f) => fmtDate(f.details?.activityActualStart) },
    { key: 'predEarlyFinish',    label: 'Pred Early Finish', extract: (f) => fmtDate(f.details?.predecessorEarlyFinish || f.details?.predecessorActualEnd) },
  ],
  28: [
    { key: 'assignedWbs',        label: 'Assigned WBS',    extract: (f) => f.details?.rootWbsName ? `${f.details.rootWbsName} (project root)` : 'project root' },
  ],
  29: [
    { key: 'wbsLevel',           label: 'WBS Level',       extract: (f) => f.details?.depth ?? null, align: 'right' },
  ],
  30: [
    { key: 'wbsNodeName',        label: 'WBS Node Name',   extract: (f) => f.taskCode ?? '' },
  ],
  31: [],
  32: [],
  33: [
    { key: 'baselineDuration',   label: 'Baseline Dur (h)', extract: (f) => f.details?.baselineDuration ?? null, align: 'right' },
    { key: 'currentDuration',    label: 'Current Dur (h)',  extract: (f) => f.details?.currentDuration ?? null, align: 'right' },
    { key: 'changePct',          label: 'Change %',         extract: (f) => f.details?.changePct ?? null, align: 'right' },
  ],
  34: [
    { key: 'logicChange',        label: 'Change Type',      extract: (f) => f.details?.logicChange ?? '' },
    { key: 'predecessorCode',    label: 'Predecessor',      extract: (f) => f.details?.predecessorCode ?? '' },
  ],
  35: [
    { key: 'baselineFinish',     label: 'Baseline Finish',  extract: (f) => fmtDate(f.details?.baselineFinish) },
    { key: 'currentFinish',      label: 'Current Finish',   extract: (f) => fmtDate(f.details?.currentFinish) },
    { key: 'varianceDays',       label: 'Variance (d)',     extract: (f) => f.details?.varianceDays ?? null, align: 'right' },
  ],
  36: [],
  37: [
    { key: 'ghostType',          label: 'Ghost Type',       extract: (f) => f.details?.ghostType ?? '' },
    { key: 'ghostEntity',        label: 'Ghost Identifier', extract: (f) => f.details?.entityName || f.details?.entityId || f.taskCode || '' },
  ],
};

function friendlyTaskType(t) {
  switch (t) {
    case 'TT_Task': return 'Task';
    case 'TT_Mile': return 'Start Milestone';
    case 'TT_FinMile': return 'Finish Milestone';
    case 'TT_LOE': return 'LOE';
    case 'TT_WBS': return 'WBS Summary';
    case 'TT_Rsrc': return 'Resource Dep';
    default: return t || '';
  }
}

// ==================== PUBLIC API ====================

/**
 * Get the ordered column list for a given check number.
 * Base columns come first, followed by check-specific extras.
 *
 * Returns [] with no rows for summary-only / histogram checks (9, 19, 21, 36)
 * — those render without an activity table.
 *
 * @param {number} checkNumber — 1-37
 * @returns {Array<{key, label, extract, align?}>}
 */
// X-2/X-3: per-column render hints applied by key at ONE site (not on each
// column def) so both the PDF table partial and the dashboard <ActivityTable>
// pick them up from the shared schema:
//   - nowrap  — date cells never split a "dd Mon yyyy" value across lines (X-3).
//   - wrapOn  — arrow-joined path cells (#5 implied path, #6 loop path) break
//               ONLY at the " → " separators, never mid-ID (X-2, the F-2
//               nowrap treatment applied per ID token).
const DATE_COLUMN_KEYS = new Set([
  'constraintDate', 'logicCalcDate', 'earlyFinish', 'actualStart',
  'predEarlyFinish', 'baselineFinish', 'currentFinish',
]);
// V-22 (v3.23): the base ID columns never split a long hyphenated ID mid-token
// (e.g. `INJ-LOOP-`/`1`). Real P6 IDs are short — this is synthetic-fixture
// hygiene — and it REUSES the X-3 `nowrap:true` render hint (both renderers
// already apply it), not a new mechanism.
const ID_COLUMN_KEYS = new Set(['activityId', 'predecessorId']);
const ARROW_PATH_KEYS = new Set(['loopPath', 'impliedPath']);

export function getColumnsForCheck(checkNumber) {
  const extra = EXTRA_COLUMNS[checkNumber];
  if (extra === undefined) {
    throw new Error(`Unknown check number: ${checkNumber}`);
  }
  return [...BASE_COLUMNS, ...extra].map((col) => {
    if (DATE_COLUMN_KEYS.has(col.key) || ID_COLUMN_KEYS.has(col.key)) return { ...col, nowrap: true };
    if (ARROW_PATH_KEYS.has(col.key)) return { ...col, wrapOn: ' → ' };
    return col;
  });
}

/**
 * Checks that render no activity table at all (summary / histogram).
 */
// 10D-1.5/Sub-2 fix-up X16 (Path A): #36 routes through the standard
// activity_level → 'table' variant. The unloaded activities render as a
// 5-col table with truncation; the full list lives in the embedded XLSX, not the gray summary chip.
export const NO_TABLE_CHECKS = new Set([9, 19, 21, 22]);

/**
 * Report-only checks (no score, no PASS/FAIL badge, no threshold).
 */
export const REPORT_ONLY_CHECKS = new Set([5, 19]);

/**
 * Build a 2D array of raw (unformatted) row values for a single check — the
 * canonical representation shared between PDF table rendering and XLSX export.
 *
 * @param {number} checkNumber
 * @param {Array<object>} findings
 * @returns {{ columns: Array, rows: Array<Array<any>> }}
 */
export function buildTableData(checkNumber, findings) {
  const columns = getColumnsForCheck(checkNumber);
  const rows = (findings || []).map((f) =>
    columns.map((col) => {
      try {
        return col.extract(f);
      } catch {
        return '';
      }
    }),
  );
  return { columns, rows };
}
